import { Card, Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { type Shift, shiftPeriodToTimeSlot } from "@src/appV2/Shifts/Shift/types";
import {
  type GetShiftTimeProposalResponse,
  type ShiftTimeProposalStatus,
} from "@src/appV2/Shifts/ShiftTimeProposals/types";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { parseISO } from "date-fns";

import { ShiftTimeProposalCardActions } from "../../Chat/ShiftTimeProposalMessage/ShiftTimeProposalCardActions";
import { ShiftCardDivider } from "../Shift/Card/CardDivider";
import { BookingCardContentWrapper } from "../Shift/CardContentWrapper";
import { useShiftDurationWithBreak } from "../Shift/hooks/useShiftDurationWithBreak";
import { ShiftCardSummaryInfo } from "../Shift/Open/CardSummaryInfo";
import { ShiftCardPillsWrapper } from "../Shift/PillsWrapper";
import { TimeSlotPill } from "../Shift/TimeSlotPill";
import { getShiftWorkDurationInHours } from "../utils/getShiftWorkDuration";
import { ShiftTimeProposalStatusPill } from "./ShiftTimeProposalStatusPill";

interface ShiftTimeProposalMessageCardProps {
  originalStartTime: string;
  originalEndTime: string;
  shiftTimeProposal: GetShiftTimeProposalResponse;
  shift: Shift;
  isUpdateShiftTimeProposalLoading?: boolean;
  updatedStatus?: ShiftTimeProposalStatus;
  onAccept?: () => Promise<void>;
  onDecline?: () => Promise<void>;
}

export function ShiftTimeProposalMessageCard({
  originalStartTime,
  originalEndTime,
  shiftTimeProposal,
  shift,
  isUpdateShiftTimeProposalLoading = false,
  updatedStatus,
  onAccept,
  onDecline,
}: ShiftTimeProposalMessageCardProps) {
  const worker = useDefinedWorker();
  const proposedStartTime = shiftTimeProposal.data.attributes?.proposedStartTime;
  const proposedEndTime = shiftTimeProposal.data.attributes?.proposedEndTime;
  const status = shiftTimeProposal?.data?.attributes?.status;

  const now = new Date();
  const isProposalExpired =
    isDefined(proposedStartTime) &&
    (now > parseISO(proposedStartTime) ||
      isDefined(shift.clockInOut?.start) ||
      shift.agentId !== worker.userId ||
      shift.deleted === true);

  const isProposalActionable = isDefined(status) && status === "PROPOSED" && !isProposalExpired;

  const proposedDateRange = {
    startDate: parseISO(proposedStartTime),
    endDate: parseISO(proposedEndTime),
  };

  const originalDateRange = {
    startDate: parseISO(originalStartTime),
    endDate: parseISO(originalEndTime),
  };

  const requiresLunchBreak = shift.facility.requiresLunchBreak ?? false;

  const { mandatoryBreakDurationInMinutes } = useShiftDurationWithBreak({
    startDate: proposedDateRange.startDate,
    endDate: proposedDateRange.endDate,
    workplaceId: shift.facility.userId ?? "",
    requiresLunchBreak,
  });

  const proposedShiftWorkDurationInHours = getShiftWorkDurationInHours(
    { start: proposedStartTime, end: proposedEndTime },
    requiresLunchBreak,
    mandatoryBreakDurationInMinutes
  );

  const originalShiftWorkDurationInHours = getShiftWorkDurationInHours(
    { start: originalStartTime, end: originalEndTime },
    requiresLunchBreak,
    mandatoryBreakDurationInMinutes
  );

  const deviceTimezone = new Intl.DateTimeFormat().resolvedOptions().timeZone;
  const facilityTimezone = shift.facility.tmz;
  const timeSlot = shiftPeriodToTimeSlot[shift.name];
  const pillStatus =
    isDefined(updatedStatus) && isUpdateShiftTimeProposalLoading ? updatedStatus : status;

  return (
    <Card outlined>
      <BookingCardContentWrapper sx={{ gap: 0 }}>
        <Stack gap={5}>
          <ShiftCardPillsWrapper>
            <TimeSlotPill timeSlot={timeSlot} />
            <ShiftTimeProposalStatusPill status={pillStatus} expired={isProposalExpired} />
          </ShiftCardPillsWrapper>

          {/* Proposed time */}
          <ShiftCardSummaryInfo
            dateRange={proposedDateRange}
            deviceTimezone={deviceTimezone}
            durationInHours={proposedShiftWorkDurationInHours}
            showBreakInfo={false}
            timezone={facilityTimezone}
          />

          {/* Original time, grayed out */}
          <Stack gap={2} sx={{ opacity: isProposalExpired ? 1 : 0.5 }}>
            <Text variant="caption">Original time</Text>
            <ShiftCardSummaryInfo
              dateRange={originalDateRange}
              deviceTimezone={deviceTimezone}
              durationInHours={originalShiftWorkDurationInHours}
              showBreakInfo={false}
              timezone={facilityTimezone}
              showDay={false}
            />
          </Stack>
        </Stack>

        {isProposalActionable && (
          <>
            <ShiftCardDivider />
            <ShiftTimeProposalCardActions
              isUpdateShiftTimeProposalLoading={isUpdateShiftTimeProposalLoading}
              onAccept={onAccept}
              onDecline={onDecline}
            />
          </>
        )}
      </BookingCardContentWrapper>
    </Card>
  );
}
